import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import type { LocalBook } from './db';
import { listBooks, putBook, addHighlight as addHl } from './db';
import { supabase, supabaseEnabled } from './supabase';
import { drainOnce, pullAllProgress } from './sync';
import { reconcileLibrary } from './library-mirror';

type Theme = 'light' | 'sepia' | 'dark';
type State = {
  session: Session | null;
  ready: boolean;
  books: LocalBook[];
  theme: Theme;
  voice: string;
  font: string;
  fontSize: number;
  autoScrollWpm: number;
  setTheme: (t: Theme) => void;
  setVoice: (v: string) => void;
  setFont: (f: string) => void;
  setFontSize: (n: number) => void;
  setAutoScrollWpm: (n: number) => void;
  refreshBooks: () => Promise<void>;
  addBook: (b: LocalBook) => Promise<void>;
  saveHighlight: (h: { book_id: string; chapter: number; page: number; text: string; note: string | null; kind: 'quote' | 'learning' }) => Promise<void>;
};

export const useStore = create<State>((set, get) => ({
  session: null,
  ready: false,
  books: [],
  theme: (localStorage.getItem('theme') as Theme) || 'light',
  voice: localStorage.getItem('voice') || 'bella',
  font: localStorage.getItem('font') || 'Fraunces',
  fontSize: Number(localStorage.getItem('fontSize')) || 16,
  autoScrollWpm: Number(localStorage.getItem('autoScrollWpm')) || 240,

  setTheme: (t) => { localStorage.setItem('theme', t); document.documentElement.classList.toggle('dark', t === 'dark'); set({ theme: t }); },
  setVoice: (v) => { localStorage.setItem('voice', v); set({ voice: v }); },
  setFont: (f) => { localStorage.setItem('font', f); set({ font: f }); },
  setFontSize: (n) => { localStorage.setItem('fontSize', String(n)); set({ fontSize: n }); },
  setAutoScrollWpm: (n) => { localStorage.setItem('autoScrollWpm', String(n)); set({ autoScrollWpm: n }); },

  refreshBooks: async () => {
    const books = await listBooks();
    set({ books });
  },

  addBook: async (b) => {
    await putBook(b);
    await get().refreshBooks();
  },

  saveHighlight: async (h) => {
    const id = crypto.randomUUID();
    await addHl({ id, ...h, status: 'pending', created_at: Date.now() });
    drainOnce();
  },
}));

export async function bootstrapStore() {
  // Theme
  document.documentElement.classList.toggle('dark', useStore.getState().theme === 'dark');

  // Auth
  if (supabaseEnabled) {
    const { data } = await supabase.auth.getSession();
    useStore.setState({ session: data.session });
    supabase.auth.onAuthStateChange((_e: string, session: Session | null) => useStore.setState({ session }));
  }

  await useStore.getState().refreshBooks();
  useStore.setState({ ready: true });

  // Background: (a) pull all progress rows from Supabase so library shows
  // the correct "X% read" bars even after site-data clears, and (b) reconcile
  // local library against SiYuan /Books tree so deletions made in Claude
  // Code propagate to the app. Non-blocking - app is usable immediately.
  (async () => {
    const updated = await pullAllProgress().catch(() => 0);
    if (updated > 0) await useStore.getState().refreshBooks();
    const mirror = await reconcileLibrary().catch(() => null);
    if (mirror) await useStore.getState().refreshBooks();
  })();
}
